/**
 * Canvas utility functions for overlay components
 */

/**
 * Hard cap on the devicePixelRatio used for canvas backing-store scaling.
 * Uncapped DPR (e.g. 4 on some Android flagships) would multiply fill cost
 * by dpr^2 for marginal visual gain; 3 matches the Goal C spec's budget
 * (docs/specs/trace-pointes-ribbon.md, C1).
 */
const MAX_CANVAS_DPR = 3;

export interface SetupCanvasOptions {
  /**
   * When provided and > 1, scales the canvas backing store by this ratio
   * (capped at MAX_CANVAS_DPR) and pre-applies `ctx.scale(dpr, dpr)` so
   * callers keep drawing in CSS pixels. Omit to keep the historical
   * behaviour (backing store == CSS pixels, no scale) — this is the default
   * for every existing caller (BodyRegionOverlay, UnifiedPoseOverlay, etc.);
   * only the trail canvas opts in (no global DPR flip).
   */
  dpr?: number;
}

/**
 * Sets up a canvas element with the specified dimensions and returns the 2D rendering context.
 * This handles the common boilerplate of:
 * - Getting the canvas element from the ref
 * - Getting the 2D context
 * - Setting canvas dimensions (backing store = CSS px, or CSS px * dpr when opted in)
 * - Clearing the canvas
 *
 * @param canvasRef - React ref to the canvas element
 * @param width - Canvas width in CSS pixels
 * @param height - Canvas height in CSS pixels
 * @param opts - Optional `{ dpr }` to scale the backing store for crisp high-DPI rendering
 * @returns The 2D rendering context, or null if setup failed
 */
export function setupCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  width: number,
  height: number,
  opts?: SetupCanvasOptions
): CanvasRenderingContext2D | null {
  const canvas = canvasRef.current;
  if (!canvas) return null;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const requestedDpr = opts?.dpr;
  const dpr = requestedDpr && requestedDpr > 1 ? Math.min(requestedDpr, MAX_CANVAS_DPR) : null;

  if (dpr) {
    // Setting canvas.width/height always resets the bitmap AND the context's
    // transformation matrix to identity, so this scale() call is never
    // compounded across repeated setupCanvas() calls on the same context.
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.scale(dpr, dpr);
  } else {
    canvas.width = width;
    canvas.height = height;
  }
  // Callers always draw (and clear) in CSS px; the scale() above (if any)
  // maps that onto the higher-resolution backing store.
  ctx.clearRect(0, 0, width, height);

  return ctx;
}

/**
 * Safely executes a canvas drawing function with error handling.
 * Wraps drawing operations in a try-catch block to prevent rendering errors
 * from breaking the component or application.
 *
 * @param ctx - The 2D rendering context to draw on
 * @param drawFn - The drawing function to execute
 * @param componentName - Name of the component for error logging
 */
export function safeCanvasDraw(
  ctx: CanvasRenderingContext2D,
  drawFn: () => void,
  componentName: string
): void {
  try {
    drawFn();
  } catch (error) {
    console.error(`[${componentName}] Canvas drawing error:`, error);
  }
}
